import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Sparkles, PenLine, EyeOff } from 'lucide-react';
import type { RootState } from '../../../../store/redux/store/store';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import { Spinner } from '../../../../components/ui/Spinner';
import { Switch } from '../../../../components/ui/Switch';
import InlineRecognitionForm from './InlineRecognitionForm';
import { useToast } from '../../../../hooks/useToast';
import { getBranches } from '@/api/General/Company';
import { Branch } from '@/models/general';
import { DepartmentBasicView } from '@/api/PreformanceManagement/Goals';
import {
  getRecognitionGaps,
  getRecognitionReasons,
  draftRecognition,
  RecognitionGapRowView,
  RecognitionReasonView,
  RecognitionScoreBreakdownView,
  RecognitionGroupStatView,
  toDisplayBand,
  DisplayBand,
} from '@/api/PreformanceManagement/Recognition';

// Prefill handed to the composer when an AI draft is generated for a gap row.
export interface RecognitionGapDraftPrefill {
  recipientId: string;
  message: string;
  values: string[];
  aiDraft: { name: string; aiAvailable: boolean; noGoals: boolean };
}

// Score colours are the Q12 heatmap's scale (Q12Heatmap getScoreColor), so a department score here
// reads the same as on the dashboard. CO-915: the 65-79 step used green-500, next to green-600 for
// 80+, so a 68 looked like the 100s; the heatmap's green-400 is the distinct "Good" step. The band
// word is printed beside the number, so the meaning never rests on colour alone.
const scoreTextColor = (score: number): string => {
  if (score >= 80) return 'text-green-600';
  if (score >= 65) return 'text-green-500';
  if (score >= 50) return 'text-amber-500';
  if (score >= 35) return 'text-amber-400';
  return 'text-red-500';
};

const scoreBarColor = (score: number): string => {
  if (score >= 80) return 'bg-green-600';
  if (score >= 65) return 'bg-green-400';
  if (score >= 50) return 'bg-amber-500';
  if (score >= 35) return 'bg-amber-400';
  return 'bg-red-500';
};

const scoreBandKey = (score: number): string => {
  if (score >= 80) return 'insights.q12.excellent';
  if (score >= 65) return 'insights.q12.good';
  if (score >= 50) return 'insights.q12.moderate';
  if (score >= 35) return 'insights.q12.needsAttention';
  return 'insights.q12.critical';
};

interface RecognitionGapsTabProps {
  // Department is owned by the parent (shared FilterBar); the tab renders the control but the parent holds the state.
  departmentId?: number;
  departments?: DepartmentBasicView[];
  onDepartmentChange?: (departmentId?: number) => void;
}

// The band filter operates only on the coarse display band — never a date. 'all' shows every band.
type BandFilter = DisplayBand | 'all';

const RecognitionGapsTab: React.FC<RecognitionGapsTabProps> = ({
  departmentId,
  departments = [],
  onDepartmentChange,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const companyId = useSelector((state: RootState) => state.user.company?.id as number | undefined);

  // Branch is a tab-owned, server-side filter. Band is a tab-owned, client-side filter.
  const [branches, setBranches] = useState<Branch[]>([]);
  const [branchValue, setBranchValue] = useState<string>('all');
  const [bandFilter, setBandFilter] = useState<BandFilter>('all');

  const [rows, setRows] = useState<RecognitionGapRowView[]>([]);
  // Per-display-band counts derived worst→best; no dates, only coarse buckets.
  const [counts, setCounts] = useState<Record<DisplayBand, number>>({
    never: 0,
    gt30: 0,
    le30: 0,
    le15: 0,
    le7: 0,
  });
  const [scoreBreakdown, setScoreBreakdown] = useState<RecognitionScoreBreakdownView | undefined>();
  const [loading, setLoading] = useState(true);
  // A refetch (scope toggle, filter change) busies only the list — the first load owns `loading`,
  // which swaps out the whole tab and would unmount the control the user just clicked.
  const [refreshing, setRefreshing] = useState(false);
  const loadedOnceRef = useRef(false);
  const [forbidden, setForbidden] = useState(false);
  const [reasons, setReasons] = useState<Record<string, RecognitionReasonView>>({});
  const [reasonsLoading, setReasonsLoading] = useState<Record<string, boolean>>({});
  const [draftLoading, setDraftLoading] = useState<Record<string, boolean>>({});

  // Opt-in department view. Server-side (it refetches); the server clamps it to the caller's
  // own department, so this can never widen further than that.
  // Bumped after an inline send so the list re-reads and the person moves band on the spot.
  const [refreshTick, setRefreshTick] = useState(0);
  // Which row currently has the compact recognition form open, and the draft that filled it.
  // One at a time: two open forms under different people invites sending to the wrong one.
  const [inlineOpenFor, setInlineOpenFor] = useState<string | null>(null);
  const [inlineDraft, setInlineDraft] = useState<
    Record<string, { message: string; aiDraft: { name: string; aiAvailable: boolean; noGoals: boolean } }>
  >({});

  const [includeDepartment, setIncludeDepartment] = useState(false);
  // Defaults assume reports exist so the tab never accuses a caller of having none before the flag arrives.
  const [hasDirectReports, setHasDirectReports] = useState(true);
  const [hasDepartmentPeers, setHasDepartmentPeers] = useState(false);
  // True for HR/Admin/Chief: the list is the whole company, so no team-scoped copy applies.
  const [isCompanyScope, setIsCompanyScope] = useState(false);
  // The caller's own department name, sent only when they are not company-scoped. Labels the headline.
  const [scopeDepartmentName, setScopeDepartmentName] = useState<string | undefined>(undefined);

  // Load the branch list once so the branch filter can be populated.
  useEffect(() => {
    if (companyId === undefined) return;
    let cancelled = false;
    getBranches(companyId)
      .then((result) => {
        if (!cancelled && result.errors.length === 0) setBranches(result.payload ?? []);
      })
      .catch(() => {
        /* branch filter simply stays at "All" if the list can't be loaded */
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const branchId = branchValue !== 'all' ? parseInt(branchValue, 10) : undefined;

  // Department is a shared, parent-owned filter that survives a tab switch. Only a company-wide
  // caller has a control for it here, so for anyone else it is neutralised: otherwise a value set
  // on another tab would silently empty this one with nothing on screen to explain it.
  const effectiveDepartmentId = isCompanyScope ? departmentId : undefined;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Only the very first fetch is allowed to blank the tab; everything after is a quiet refresh.
      if (loadedOnceRef.current) setRefreshing(true);
      else setLoading(true);
      try {
        const result = await getRecognitionGaps(effectiveDepartmentId, branchId, includeDepartment);
        if (cancelled) return;
        if (result.errors.length === 0) {
          const p = result.payload;
          setRows(p.rows);
          setCounts({
            never: p.neverCount,
            gt30: p.overThirtyCount,
            le30: p.withinThirtyCount + p.withinTwentyOneCount,
            le15: p.withinFourteenCount,
            le7: p.withinSevenCount,
          });
          setScoreBreakdown(p.scoreBreakdown);
          setHasDirectReports(p.hasDirectReports ?? true);
          setHasDepartmentPeers(p.hasDepartmentPeers ?? false);
          setIsCompanyScope(p.isCompanyScope ?? false);
          setScopeDepartmentName(p.scopeDepartmentName);
          setForbidden(false);
        } else {
          setForbidden(true);
        }
      } catch {
        if (!cancelled) setForbidden(true);
      } finally {
        // Cancelled means a newer fetch is already in flight and owns the busy flags.
        if (!cancelled) {
          loadedOnceRef.current = true;
          setLoading(false);
          setRefreshing(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveDepartmentId, branchId, includeDepartment, refreshTick]);

  const loadReasons = useCallback(
    async (employeeId: string) => {
      setReasonsLoading((p) => ({ ...p, [employeeId]: true }));
      try {
        const result = await getRecognitionReasons(employeeId);
        if (result.errors.length === 0) {
          setReasons((p) => ({ ...p, [employeeId]: result.payload }));
        } else {
          showToast(t('performance.recognition.gaps.ai.error'), 'error');
        }
      } catch {
        showToast(t('performance.recognition.gaps.ai.error'), 'error');
      } finally {
        setReasonsLoading((p) => ({ ...p, [employeeId]: false }));
      }
    },
    [showToast, t]
  );

  // Generate an AI draft and open the compact form UNDER this row. The draft used to open
  // the full composer at the top of the page, which meant scrolling up to read it and back
  // down to find the next person — annoying enough that people stopped using it.
  const handleDraft = useCallback(
    async (employeeId: string, employeeName?: string) => {
      setDraftLoading((p) => ({ ...p, [employeeId]: true }));
      try {
        const result = await draftRecognition(employeeId);
        if (result.errors.length === 0 && result.payload) {
          setInlineDraft((p) => ({
            ...p,
            [employeeId]: {
              message: result.payload.message,
              aiDraft: {
                name: employeeName ?? '',
                aiAvailable: result.payload.aiAvailable,
                noGoals: result.payload.noGoals,
              },
            },
          }));
          setInlineOpenFor(employeeId);
        } else {
          showToast(t('performance.recognition.gaps.draftFailed'), 'error');
        }
      } catch {
        showToast(t('performance.recognition.gaps.draftFailed'), 'error');
      } finally {
        setDraftLoading((p) => ({ ...p, [employeeId]: false }));
      }
    },
    [showToast, t]
  );

  const renderRow = (row: RecognitionGapRowView) => (
    <div
      key={row.employeeId}
      className="rounded-lg border border-border bg-card p-4"
      data-testid={`recognition-gap-row-${row.employeeId}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{row.employeeName}</p>
          {row.departmentName && (
            <p className="text-xs text-muted-foreground truncate">{row.departmentName}</p>
          )}
        </div>
        <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => loadReasons(row.employeeId)}
            disabled={!!reasonsLoading[row.employeeId]}
            data-testid={`recognition-gap-ai-btn-${row.employeeId}`}
          >
            <Sparkles className="w-3.5 h-3.5 me-1.5" />
            {reasonsLoading[row.employeeId]
              ? t('performance.recognition.gaps.ai.loading')
              : reasons[row.employeeId]
              ? t('performance.recognition.gaps.ai.refresh')
              : t('performance.recognition.gaps.ai.generate')}
          </Button>
          {/* Write it yourself, without waiting on a draft. */}
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              setInlineOpenFor((cur) => (cur === row.employeeId ? null : row.employeeId))
            }
            data-testid={`recognition-gap-recognize-btn-${row.employeeId}`}
          >
            {t('performance.recognition.gaps.inline.recognize')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleDraft(row.employeeId, row.employeeName)}
            disabled={!!draftLoading[row.employeeId]}
            data-testid={`recognition-gap-draft-btn-${row.employeeId}`}
          >
            <PenLine className="w-3.5 h-3.5 me-1.5" />
            {draftLoading[row.employeeId]
              ? t('performance.recognition.gaps.ai.loading')
              : t('performance.recognition.gaps.generateRecognition')}
          </Button>
        </div>
      </div>

      {/* The form opens where the person already is, so nobody has to scroll away and back. */}
      {inlineOpenFor === row.employeeId && (
        <InlineRecognitionForm
          key={`${row.employeeId}-${inlineDraft[row.employeeId]?.message ?? ''}`}
          recipientId={row.employeeId}
          recipientName={row.employeeName ?? ''}
          initialMessage={inlineDraft[row.employeeId]?.message ?? ''}
          aiDraft={inlineDraft[row.employeeId]?.aiDraft ?? null}
          onCancel={() => setInlineOpenFor(null)}
          onSent={() => {
            setInlineOpenFor(null);
            setInlineDraft((p) => {
              const next = { ...p };
              delete next[row.employeeId];
              return next;
            });
            showToast(t('performance.recognition.gaps.inline.sent', { name: row.employeeName ?? '' }), 'success');
            // Re-read so the person moves to their new band immediately.
            setRefreshTick((n) => n + 1);
          }}
        />
      )}

      {/* The AI panel sits below the band and never mutates it. */}
      {reasons[row.employeeId] && (
        <div className="mt-3 space-y-3" data-testid={`recognition-gap-ai-panel-${row.employeeId}`}>
          {reasons[row.employeeId].noGoals ? (
            // No completed goals: a neutral note — never the "AI is off" banner, even with AI on.
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              {t('performance.recognition.gaps.ai.noGoals')}
            </p>
          ) : (
            <>
              <p className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                {reasons[row.employeeId].aiAvailable
                  ? t('performance.recognition.gaps.ai.disclaimer')
                  : t('performance.recognition.gaps.ai.disclaimerFallback')}
              </p>
              <p className="text-sm text-foreground">{reasons[row.employeeId].summary}</p>
              {reasons[row.employeeId].reasons.length > 0 ? (
                <ul className="space-y-2">
                  {reasons[row.employeeId].reasons.map((reason, i) => (
                    <li
                      key={`${reason.title}-${i}`}
                      className="rounded-md border border-border bg-background p-2.5"
                      data-testid={`recognition-gap-ai-reason-${row.employeeId}-${i}`}
                    >
                      <p className="text-xs font-medium text-foreground">{reason.title}</p>
                      <p className="text-xs text-muted-foreground">{reason.detail}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('performance.recognition.gaps.ai.noReasons')}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );

  // Per-group rows (department or branch) with a sub-factor bar, mirroring the dashboard drill-down style.
  // The empty-name label is a parameter so the branch table can say "no branch", not "no department".
  const renderGroupBreakdown = (
    stats: RecognitionGroupStatView[],
    testId: string,
    emptyNameKey = 'performance.recognition.gaps.noDepartment'
  ) => (
    <div className="space-y-3" data-testid={testId}>
      {stats.map((d, index) => {
        // The API omits null names, so the ungrouped bucket arrives as absent, never as null.
        const name = d.name || t(emptyNameKey);
        const barWidth = Math.max(0, Math.min(100, d.score));
        // CO-915: a group under the 5-person floor shows no score and no counts, as on the heatmap.
        // CO932-3: a group hidden by the secondary rule has 5+ people, so it gets its own wording; a
        // missing reason (older payloads) keeps the under-floor one.
        if (d.hidden) {
          const bySubtraction = d.hiddenReason === 'subtraction';
          return (
            <div key={`${d.name || 'none'}-${index}`} data-testid={`${testId}-${index}`}>
              <div className="mb-1 flex items-center justify-between gap-3">
                <span className="text-sm text-foreground truncate">{name}</span>
                <span
                  className="flex items-center gap-1 text-xs text-muted-foreground"
                  data-testid={`${testId}-${index}-hidden`}
                  data-hidden-reason={bySubtraction ? 'subtraction' : 'floor'}
                  title={bySubtraction ? t('insights.q12.hiddenBySubtraction') : t('insights.q12.hiddenTooltip')}
                >
                  <EyeOff size={12} aria-hidden="true" />
                  {bySubtraction ? t('insights.q12.hiddenBySubtraction') : t('performance.recognition.gaps.groupHidden')}
                </span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-gray-100 dark:bg-gray-800" />
            </div>
          );
        }
        return (
          <div key={`${d.name || 'none'}-${index}`} data-testid={`${testId}-${index}`}>
            <div className="mb-1 flex items-center justify-between gap-3">
              <span className="text-sm text-foreground truncate">{name}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {t('performance.recognition.gaps.deptStat', {
                    recognized: d.recognizedCount,
                    notRecognized: d.notRecognizedCount,
                  })}
                </span>
                <span className="text-xs text-muted-foreground" data-testid={`${testId}-${index}-band`}>
                  {t(scoreBandKey(d.score))}
                </span>
                <span className={`text-sm font-semibold ${scoreTextColor(d.score)}`}>
                  {d.score}
                </span>
              </div>
            </div>
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div
                className={`h-full rounded-full transition-all duration-500 ${scoreBarColor(d.score)}`}
                style={{ width: `${barWidth}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );

  // Headline score card mirroring the Q12 drill-down "Overall score" card.
  const renderScoreCard = (score: number, label: string, note: string | null, testId: string) => (
    <div className="flex items-center gap-3 rounded-lg bg-muted p-3" data-testid={testId}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        {note && <p className="text-xs text-muted-foreground">{note}</p>}
      </div>
      <div className="ms-auto flex items-center gap-2">
        <span className={`text-2xl font-bold ${scoreTextColor(score)}`}>{score}</span>
        <span className="text-xs text-muted-foreground">
          {t('performance.recognition.gaps.scoreOutOf')}
        </span>
      </div>
    </div>
  );

  // The company number is a fixed point of comparison, kept deliberately quiet so it never
  // competes with the headline it sits under.
  const renderCompanyReference = (score: number) => (
    <p className="text-xs text-muted-foreground" data-testid="gap-company-reference">
      {t('performance.recognition.gaps.companyReference', { score })}
    </p>
  );

  // Takes the headline card's slot when a filter matches nobody, so the region says why it is
  // empty instead of leaving the reason to be inferred from the list below.
  const renderNoneMatchFilters = () => (
    <p className="rounded-lg bg-muted p-3 text-sm text-muted-foreground" data-testid="gap-none-match-filters">
      {t('performance.recognition.gaps.noneMatchFilters')}
    </p>
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="recognition-gaps-loading">
        <Spinner />
      </div>
    );
  }

  if (forbidden) {
    return (
      <p className="py-12 text-center text-sm text-muted-foreground" data-testid="recognition-gaps-forbidden">
        {t('performance.recognition.gaps.forbidden')}
      </p>
    );
  }

  // Worst→best display order; label keys are the coarse band only — never a date or day count.
  const bands: { key: DisplayBand; labelKey: string }[] = [
    { key: 'never', labelKey: 'performance.recognition.gaps.bandNever' },
    { key: 'gt30', labelKey: 'performance.recognition.gaps.bandGt30' },
    { key: 'le30', labelKey: 'performance.recognition.gaps.bandLe30' },
    { key: 'le15', labelKey: 'performance.recognition.gaps.bandLe15' },
    { key: 'le7', labelKey: 'performance.recognition.gaps.bandLe7' },
  ];

  const rowsByBand = bands.reduce<Record<DisplayBand, RecognitionGapRowView[]>>(
    (acc, { key }) => {
      acc[key] = rows.filter((r) => toDisplayBand(r.band) === key);
      return acc;
    },
    { never: [], gt30: [], le30: [], le15: [], le7: [] }
  );

  // Band is filtered client-side: 'all' keeps every section, otherwise only the chosen band's section renders.
  const visibleBands = bandFilter === 'all' ? bands : bands.filter((b) => b.key === bandFilter);

  const branchOptions = [
    { value: 'all', label: t('performance.recognition.gaps.filterAllBranches') },
    ...branches.map((b) => ({ value: String(b.id), label: b.name })),
  ];
  const departmentOptions = [
    { value: 'all', label: t('performance.recognition.gaps.filterAllDepartments') },
    ...departments.map((d) => ({ value: String(d.id), label: d.name })),
  ];
  const bandOptions = [
    { value: 'all', label: t('performance.recognition.gaps.filterAllBands') },
    ...bands.map((b) => ({ value: b.key, label: t(b.labelKey) })),
  ];
  const departmentValue = effectiveDepartmentId !== undefined ? String(effectiveDepartmentId) : 'all';

  // A manager narrows by department through the scope toggle; a company-wide caller through the dropdown.
  const selectedBranchLabel = branchOptions.find((o) => o.value === branchValue)?.label;
  const selectedDeptLabel = isCompanyScope
    ? departments.find((d) => d.id === effectiveDepartmentId)?.name
    : includeDepartment
    ? scopeDepartmentName
    : undefined;

  // Read from the controls, never from whether a display name resolved: an archived department
  // still yields department peers while its name comes back absent, and a label that failed to
  // resolve must not decide whether the population was narrowed.
  // Band never participates: it filters the list, not the score.
  const isFiltered =
    branchValue !== 'all' || (isCompanyScope ? effectiveDepartmentId !== undefined : includeDepartment);

  const headlineParts = [selectedDeptLabel, branchValue !== 'all' ? selectedBranchLabel : undefined]
    .filter(Boolean)
    .join(' · ');
  // A narrowed view whose name could not be resolved still needs a label; falling through would
  // render a card with a blank one.
  const headlineLabel = headlineParts || t('performance.recognition.gaps.filteredScoreLabel');

  // An empty population scores 0, which would read as "nobody is recognized" rather than
  // "nobody matched". The number is withheld and the reason named in its place.
  const hasPopulation = rows.length > 0;

  const filtersActive =
    branchValue !== 'all' ||
    bandFilter !== 'all' ||
    effectiveDepartmentId !== undefined ||
    includeDepartment;

  const clearFilters = () => {
    setBranchValue('all');
    setBandFilter('all');
    setIncludeDepartment(false);
    onDepartmentChange?.(undefined);
  };

  return (
    <div className="space-y-6" data-testid="recognition-gaps-tab">
      {/* Controls come first: the score below describes whatever they select.
          Branch + department drive the server fetch; band is client-side (coarse buckets, no dates). */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div
          className={`grid grid-cols-1 gap-3 ${isCompanyScope ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
        >
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t('performance.recognition.gaps.filterBranch')}
            </label>
            <Select
              options={branchOptions}
              value={branchValue}
              onChange={(val: string) => setBranchValue(val || 'all')}
              data-testid="gap-filter-branch"
            />
          </div>
          {/* A manager's department control is the scope toggle below; two controls for one
              dimension, with different meanings, would be a trap. */}
          {isCompanyScope && (
            <div>
              <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                {t('performance.recognition.gaps.filterDepartment')}
              </label>
              <Select
                options={departmentOptions}
                value={departmentValue}
                onChange={(val: string) => onDepartmentChange?.(val === 'all' || !val ? undefined : parseInt(val, 10))}
                data-testid="gap-filter-department"
              />
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
              {t('performance.recognition.gaps.filterBand')}
            </label>
            <Select
              options={bandOptions}
              value={bandFilter}
              onChange={(val: string) => setBandFilter((val || 'all') as BandFilter)}
              data-testid="gap-filter-band"
            />
            {/* Without this, filtering to "Never recognized" and seeing a score of 0 looks like a bug. */}
            <p className="mt-1.5 text-xs text-muted-foreground" data-testid="gap-filter-band-hint">
              {t('performance.recognition.gaps.filterBandHint')}
            </p>
          </div>
        </div>
        {filtersActive && (
          <div className="mt-4 flex justify-end border-t border-border pt-3">
            <Button
              size="sm"
              variant="ghost"
              onClick={clearFilters}
              data-testid="gap-clear-filters"
            >
              {t('performance.recognition.gaps.clearFilters')}
            </Button>
          </div>
        )}
      </div>

      {/* Scope opt-in: a manager sees direct reports by default and widens to the department explicitly. */}
      {hasDepartmentPeers && (
        <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-4">
          <div>
            <p className="text-sm font-medium text-foreground">
              {t('performance.recognition.gaps.scopeToggleLabel')}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('performance.recognition.gaps.scopeToggleHint')}
            </p>
          </div>
          <Switch
            checked={includeDepartment}
            onCheckedChange={setIncludeDepartment}
            data-testid="gap-scope-department-toggle"
            aria-label={t('performance.recognition.gaps.scopeToggleLabel')}
          />
        </div>
      )}

      {/* The Q4 score this tab explains, now describing the filtered population, over a pivot
          that groups by whichever dimension the filters left open. */}
      {scoreBreakdown && (
        <div className="space-y-4">
          {isCompanyScope ? (
            <div className="space-y-2">
              {/* Unfiltered, the headline IS the company number; filtered, it re-labels to the selection. */}
              {!hasPopulation && isFiltered
                ? renderNoneMatchFilters()
                : hasPopulation &&
                  (isFiltered
                    ? renderScoreCard(
                        scoreBreakdown.filteredScore,
                        headlineLabel,
                        null,
                        'gap-headline-score'
                      )
                    : renderScoreCard(
                        scoreBreakdown.companyScore,
                        t('performance.recognition.gaps.companyScoreLabel'),
                        t('performance.recognition.gaps.companyScoreNote'),
                        'gap-headline-score'
                      ))}
              {/* Redundant while unfiltered, since the headline is already the company number. */}
              {isFiltered && renderCompanyReference(scoreBreakdown.companyScore)}
            </div>
          ) : (
            <>
              {/* Fixed points of comparison: this manager's direct reports, and the whole company.
                  Neither reacts to a filter. */}
              <div
                className="space-y-3 rounded-lg border border-border bg-card p-4"
                data-testid="gap-team-score-section"
              >
                {scoreBreakdown.teamScore != null &&
                  renderScoreCard(
                    scoreBreakdown.teamScore,
                    t('performance.recognition.gaps.teamScoreLabel'),
                    null,
                    'gap-team-score'
                  )}
                {renderCompanyReference(scoreBreakdown.companyScore)}
              </div>
              {/* Unfiltered this would only repeat the team card, so it appears once a filter narrows the view. */}
              {isFiltered &&
                (hasPopulation
                  ? renderScoreCard(
                      scoreBreakdown.filteredScore,
                      headlineLabel,
                      null,
                      'gap-headline-score'
                    )
                  : renderNoneMatchFilters())}
            </>
          )}

          {/* Exactly one of these is ever populated: the server pivots on the open dimension. */}
          {scoreBreakdown.departments.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground">
                {t('performance.recognition.gaps.deptBreakdownTitle')}
              </h2>
              {renderGroupBreakdown(scoreBreakdown.departments, 'gap-dept-breakdown')}
            </section>
          )}
          {scoreBreakdown.branches.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground">
                {t('performance.recognition.gaps.branchBreakdownTitle')}
              </h2>
              {renderGroupBreakdown(
                scoreBreakdown.branches,
                'gap-branch-breakdown',
                'performance.recognition.gaps.noBranch'
              )}
            </section>
          )}
        </div>
      )}

      {/* Required explanatory copy (spec §7.2). Without the third sentence this tab is harmful. */}
      <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-1.5">
        <p className="text-xs text-muted-foreground">{t('performance.recognition.gaps.explainPrivate')}</p>
        <p className="text-xs text-muted-foreground">{t('performance.recognition.gaps.explainHidden')}</p>
        <p className="text-xs text-muted-foreground">{t('performance.recognition.gaps.explainAbsence')}</p>
        {/* CO-938 (owner 2026-10-29): a per-person worklist; the floor governs only the group pivot. */}
        <p className="text-xs text-muted-foreground" data-testid="gap-explain-floor">
          {t('performance.recognition.gaps.explainFloor')}
        </p>
      </div>

      {/* Names the population on screen so "team" is never ambiguous. Three distinct callers:
          a company-wide viewer, a manager who opted into their department, and a manager
          seeing their own reports. */}
      <p className="text-xs text-muted-foreground" data-testid="gap-scope-caption">
        {isCompanyScope
          ? t('performance.recognition.gaps.showingCompany')
          : includeDepartment
          ? t('performance.recognition.gaps.showingDepartment')
          : t('performance.recognition.gaps.showingTeam')}
      </p>

      {/* Only the list goes quiet while a refetch is in flight; the score cards, scope toggle and
          filters above stay mounted so the control that triggered it keeps focus. */}
      <div
        className={`space-y-6 transition-opacity ${refreshing ? 'opacity-60' : ''}`}
        aria-busy={refreshing}
        data-testid="recognition-gaps-list-region"
      >
        {rows.length === 0 ? (
          // An empty list means either "everyone is recognized" or "nobody reports to you" — never the same message.
          // Manager-specific message only. `hasDepartmentPeers` is required because the body text
          // offers "show your full department instead", and the toggle that does that is itself
          // gated on the same flag — without this, the copy would point at a control that isn't
          // on screen. A company-wide caller falls through to the ordinary empty message.
          !isCompanyScope && !hasDirectReports && !includeDepartment && hasDepartmentPeers ? (
            <div className="py-12 text-center" data-testid="recognition-gaps-no-reports">
              <p className="text-sm font-medium text-foreground">
                {t('performance.recognition.gaps.noReportsTitle')}
              </p>
              <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">
                {t('performance.recognition.gaps.noReportsBody')}
              </p>
            </div>
          ) : (
            <p className="py-12 text-center text-sm text-muted-foreground" data-testid="recognition-gaps-empty">
              {t('performance.recognition.gaps.empty')}
            </p>
          )
        ) : (
          visibleBands.map(({ key, labelKey }) => (
            <section key={key} className="space-y-3" data-testid={`recognition-band-${key}`}>
              {/* h2: the page supplies the h1, so the tab's section headings are h2 (no skipped level). */}
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-foreground">{t(labelKey)}</h2>
                <span className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                  {counts[key]}
                </span>
              </div>
              {rowsByBand[key].length > 0 ? (
                rowsByBand[key].map(renderRow)
              ) : (
                <p className="text-xs text-muted-foreground">{t('performance.recognition.gaps.noneInBand')}</p>
              )}
            </section>
          ))
        )}
      </div>
    </div>
  );
};

export default RecognitionGapsTab;
